import * as React from 'react';
import SEO from "@/components/SEO";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FareCalculator from "@/components/FareCalculator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqItems = [
  {
    question: "How accurate is the instant quote?",
    answer: "Our instant quotes are highly accurate. The price you see includes all fees and a 20% gratuity. The only time pricing may vary is for wait time beyond the included allowance or additional stops not specified in your original quote."
  },
  {
    question: "Do I need to call to get a quote?",
    answer: "No, our instant quote calculator provides your fare in seconds—24/7, no phone calls required. Simply enter your pickup and drop-off locations, date, and time to receive an accurate price immediately."
  },
  {
    question: "Is the gratuity included in the quote?",
    answer: "Yes, all quotes include a 20% gratuity. The price you see is the complete price you'll pay—no hidden fees or surprise charges."
  },
  {
    question: "How do I book after getting my quote?",
    answer: "After receiving your quote, click the 'Book This Quote' button and enter your contact information. A team member will review your request and confirm availability, typically within a few hours."
  },
  {
    question: "Can I get a quote for multiple stops?",
    answer: "Yes, our quote calculator allows you to add multiple stops to your trip. Each additional stop is factored into your total fare, so you'll see the complete price upfront."
  }
];

const schema = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Instant Black Car Service Quote",
    "name": "Get an Instant Quote",
    "description": "Get an instant fare estimate for luxury black car transportation in San Antonio. No waiting, no calls needed.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Rick's Black Car Service",
      "telephone": "210-296-7409",
      "email": "ricksblackcarservice@outlook.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "16034 Rough Oak Street",
        "addressLocality": "San Antonio",
        "addressRegion": "TX",
        "postalCode": "78232",
        "addressCountry": "US"
      },
      "areaServed": ["San Antonio", "Boerne", "New Braunfels", "Austin", "Canyon Lake", "Seguin"],
      "priceRange": "$$"
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How accurate is the instant quote?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our instant quotes are highly accurate. The price you see includes all fees and a 20% gratuity. The only time pricing may vary is for wait time beyond the included allowance or additional stops not specified in your original quote."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need to call to get a quote?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No, our instant quote calculator provides your fare in seconds—24/7, no phone calls required. Simply enter your pickup and drop-off locations, date, and time to receive an accurate price immediately."
        }
      },
      {
        "@type": "Question",
        "name": "Is the gratuity included in the quote?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, all quotes include a 20% gratuity. The price you see is the complete price you'll pay—no hidden fees or surprise charges."
        }
      },
      {
        "@type": "Question",
        "name": "How do I book after getting my quote?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "After receiving your quote, click the 'Book This Quote' button and enter your contact information. A team member will review your request and confirm availability, typically within a few hours."
        }
      },
      {
        "@type": "Question",
        "name": "Can I get a quote for multiple stops?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, our quote calculator allows you to add multiple stops to your trip. Each additional stop is factored into your total fare, so you'll see the complete price upfront."
        }
      }
    ]
  }
];

export default function RequestQuote() {
  return (
    <>
      <SEO
        title="Get an Instant Quote | Luxury Black Car Service San Antonio | Rick's"
        description="Get an instant fare quote in seconds! No waiting, no phone calls. Luxury 2025 Audi Q7 SUV service for airport transfers, corporate travel & special events. Prices include 20% gratuity."
        canonicalPath="/request-quote"
        schema={schema}
      />
      <Navbar />
      
      <main className="min-h-screen pt-20 pb-12 px-4 bg-[#000000]">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <header className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-[#D4A574] mb-3">
              Get an Instant Quote
            </h1>
            <p className="text-xl md:text-2xl text-white font-medium mb-3">
              Luxury 2025 Audi Q7 SUV • Transparent Pricing • No Hidden Fees
            </p>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Know your exact fare in seconds—no waiting, no phone calls required. 
              All prices include 20% gratuity.
            </p>
          </header>

          {/* Fare Calculator */}
          <FareCalculator />

          {/* Customer Testimonials */}
          <section className="mt-12 mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#D4A574] text-center mb-8">
              What Our Clients Say
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[#1F2937] border border-[#D4A574]/20 rounded-xl p-6 animate-fade-in hover:scale-[1.02] hover:border-[#D4A574]/40 transition-all duration-300" style={{ animationDelay: '0.1s', animationFillMode: 'both' }}>
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-[#D4A574] text-lg">★</span>
                  ))}
                </div>
                <p className="text-gray-300 italic mb-4">
                  "Got my quote in seconds! No phone tag, no waiting for callbacks. The price was exactly what I paid—love the transparency."
                </p>
                <p className="text-[#D4A574] font-semibold">— Michael T., Business Traveler</p>
              </div>
              
              <div className="bg-[#1F2937] border border-[#D4A574]/20 rounded-xl p-6 animate-fade-in hover:scale-[1.02] hover:border-[#D4A574]/40 transition-all duration-300" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-[#D4A574] text-lg">★</span>
                  ))}
                </div>
                <p className="text-gray-300 italic mb-4">
                  "Finally, a car service with upfront pricing! The instant quote tool made planning my airport transfer so easy."
                </p>
                <p className="text-[#D4A574] font-semibold">— Sarah K., Frequent Flyer</p>
              </div>
              
              <div className="bg-[#1F2937] border border-[#D4A574]/20 rounded-xl p-6 animate-fade-in hover:scale-[1.02] hover:border-[#D4A574]/40 transition-all duration-300" style={{ animationDelay: '0.3s', animationFillMode: 'both' }}>
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-[#D4A574] text-lg">★</span>
                  ))}
                </div>
                <p className="text-gray-300 italic mb-4">
                  "I compared quotes for multiple trips in minutes. No hidden fees, gratuity included—exactly what the calculator showed."
                </p>
                <p className="text-[#D4A574] font-semibold">— David L., Event Planner</p>
              </div>
              
              <div className="bg-[#1F2937] border border-[#D4A574]/20 rounded-xl p-6 animate-fade-in hover:scale-[1.02] hover:border-[#D4A574]/40 transition-all duration-300" style={{ animationDelay: '0.4s', animationFillMode: 'both' }}>
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-[#D4A574] text-lg">★</span>
                  ))}
                </div>
                <p className="text-gray-300 italic mb-4">
                  "Needed a quick fare estimate at midnight—got it instantly! So much better than waiting until morning to call around."
                </p>
                <p className="text-[#D4A574] font-semibold">— Jennifer M., Late-Night Booker</p>
              </div>
            </div>
          </section>

          {/* How It Works Section */}
          <section className="mt-12 mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#D4A574] text-center mb-8">
              How It Works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Step 1 */}
              <div className="bg-[#1F2937] border border-[#D4A574]/20 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-[#D4A574] text-black rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  1
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Enter Your Trip Details</h3>
                <p className="text-gray-400 text-sm">
                  Input your pickup and drop-off locations, date, time, and any additional stops.
                </p>
              </div>

              {/* Step 2 */}
              <div className="bg-[#1F2937] border border-[#D4A574]/20 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-[#D4A574] text-black rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  2
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Get Your Instant Quote</h3>
                <p className="text-gray-400 text-sm">
                  Receive an accurate fare estimate immediately. No waiting, no phone calls required.
                </p>
              </div>

              {/* Step 3 */}
              <div className="bg-[#1F2937] border border-[#D4A574]/20 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-[#D4A574] text-black rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  3
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Book Your Ride</h3>
                <p className="text-gray-400 text-sm">
                  Click "Book This Quote" to send us your request. A team member will confirm availability.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mt-12 mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#D4A574] text-center mb-8">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="space-y-3">
              {faqItems.map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-[#1F2937] border border-[#D4A574]/20 rounded-xl px-6 data-[state=open]:border-[#D4A574]/40 transition-colors"
                >
                  <AccordionTrigger className="text-white hover:text-[#D4A574] text-left py-5 hover:no-underline">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-300 pb-5">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* Contact Info */}
          <div className="mt-8 text-center space-y-4">
            <p className="text-gray-400">
              Need a custom quote or have questions?
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:210-296-7409"
                className="inline-flex items-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                📞 Call 210-296-7409
              </a>
              <a
                href="mailto:ricksblackcarservice@outlook.com"
                className="inline-flex items-center gap-2 bg-[#1F2937] hover:bg-[#374151] text-white px-6 py-3 rounded-lg font-semibold transition-colors border border-[#4B5563]"
              >
                ✉️ Email Us
              </a>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </>
  );
}
